// Durable persistence for the floor outbox (spec 06 §9.2, D-048).
//
// The queue ENGINE (`floor-outbox.ts`) never talks to IndexedDB directly: it
// talks to this `OutboxStore` port. Two reasons, both structural rather than
// stylistic:
//
//  1. D-048 says the TRANSPORT moves to Module 18's sync protocol later while
//     the domain idempotency stays here. A port on the persistence side keeps
//     the same seam on the storage side: swapping IndexedDB for whatever 18
//     brings is one adapter, not a rewrite of the replay policy.
//  2. It makes the engine testable against a real, observable store rather than
//     a mock of one — the store contract test below runs the SAME suite against
//     the in-memory adapter and against a hand-written fake IndexedDB, so the
//     adapter that actually ships on a phone is covered too.
//
// DURABILITY IS THE POINT. `put()` must not resolve until the record is
// committed, because the engine's kill-safety argument depends on "queued"
// meaning "on disk": the item is written BEFORE the request is issued and
// deleted only AFTER a confirmed 2xx, so a process death at any instant leaves
// the item replayable and the server's `(tenant_id, idempotency_key)` UNIQUE
// (§9.2) makes the replay a no-op. An adapter that acknowledged early would
// turn exactly-once into at-most-once and silently lose a cut.

/** A stored outbox record. The engine owns the shape; the store owns the bytes. */
export interface OutboxRecord {
  /** uuid v7, minted at TAP time. Primary key AND the `Idempotency-Key`. */
  readonly clientKey: string;
  [field: string]: unknown;
}

/**
 * The persistence port. Every method is async because IndexedDB is; the memory
 * adapter resolves immediately but keeps the same signature so no call site can
 * accidentally depend on synchronous storage.
 */
export interface OutboxStore {
  /** Every record, in insertion order (the store preserves the engine's FIFO). */
  readAll(): Promise<OutboxRecord[]>;
  /** Insert or replace by `clientKey`. Resolves only once durable. */
  put(record: OutboxRecord): Promise<void>;
  /** Remove by `clientKey`. A missing key is not an error (idempotent delete). */
  remove(clientKey: string): Promise<void>;
  /** Drop everything (device hand-over / sign-out). */
  clear(): Promise<void>;
}

// ---------------------------------------------------------------------------
// In-memory adapter
// ---------------------------------------------------------------------------

/**
 * Volatile store. Used by the unit tests AND as the runtime fallback when
 * IndexedDB is unavailable (private-mode Safari, a locked-down WebView, a
 * storage quota refusal). The fallback is deliberately LOUD at the call site
 * (`openOutboxStore` reports `durable: false`) so the chrome can tell the
 * operator their queue will not survive a reload — a silent downgrade would let
 * a worker walk into a dead zone believing their taps were safe.
 */
export function memoryOutboxStore(): OutboxStore {
  const rows = new Map<string, OutboxRecord>();
  return {
    readAll: () => Promise.resolve([...rows.values()]),
    put: (record) => {
      rows.set(record.clientKey, record);
      return Promise.resolve();
    },
    remove: (clientKey) => {
      rows.delete(clientKey);
      return Promise.resolve();
    },
    clear: () => {
      rows.clear();
      return Promise.resolve();
    },
  };
}

// ---------------------------------------------------------------------------
// IndexedDB adapter
// ---------------------------------------------------------------------------

export const OUTBOX_DB_NAME = "assessly.floor";
/**
 * v2 (D-096) adds the `photos` object store — the durable BINARY queue that
 * rides beside the event outbox so a defect-flag's evidence photo survives a
 * reload and an airplane-mode shift exactly as the flag itself does. The
 * upgrade path from v1 creates the new store and touches nothing in `outbox`,
 * so a device with a queued shift's work upgrades in place, losing nothing.
 */
export const OUTBOX_DB_VERSION = 2;
export const OUTBOX_STORE_NAME = "outbox";
export const PHOTO_STORE_NAME = "photos";

/** The subset of the IndexedDB API this adapter uses (so it can be faked). */
export interface IdbFactoryLike {
  open(name: string, version?: number): IDBOpenDBRequest;
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error("indexeddb request failed"));
    };
  });
}

/**
 * Wait for the TRANSACTION, not just the request. A write is durable when its
 * transaction COMPLETES; resolving on `request.onsuccess` would acknowledge a
 * put that a later abort could still roll back — the exact early-ack failure
 * the header warns about.
 */
function transactionDone(txn: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    txn.oncomplete = () => {
      resolve();
    };
    txn.onerror = () => {
      reject(txn.error ?? new Error("indexeddb transaction failed"));
    };
    txn.onabort = () => {
      reject(txn.error ?? new Error("indexeddb transaction aborted"));
    };
  });
}

export function openIndexedDb(factory: IdbFactoryLike): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = factory.open(OUTBOX_DB_NAME, OUTBOX_DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(OUTBOX_STORE_NAME)) {
        // Keyed by `clientKey` — the record's key IS its `Idempotency-Key`, so
        // the two can never drift apart and a replayed put is an overwrite of
        // the same row rather than a second one.
        //
        // NOTE ON ORDERING: `getAll()` therefore returns rows in clientKey
        // order, NOT insertion order. FIFO is the ENGINE's `seq`, sorted in
        // `recompute()`; this store makes no ordering promise and a comment
        // claiming otherwise would hide the fact that a row with a missing
        // `seq` must be refused rather than defaulted (see `fromRecord`).
        db.createObjectStore(OUTBOX_STORE_NAME, { keyPath: "clientKey" });
      }
      if (!db.objectStoreNames.contains(PHOTO_STORE_NAME)) {
        // The binary sibling (D-096): evidence blobs keyed by their own tap-time
        // uuid v7. The outbox record references photos by id rather than
        // embedding the Blob, so the (large) bytes are written once and the
        // (small) event record keeps its own durability path.
        db.createObjectStore(PHOTO_STORE_NAME, { keyPath: "photoId" });
      }
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error("indexeddb open failed"));
    };
  });
}

/** IndexedDB-backed store. `db` is injected so the fake can drive the same code. */
export function indexedDbOutboxStore(db: IDBDatabase): OutboxStore {
  const write = async (apply: (store: IDBObjectStore) => void): Promise<void> => {
    const txn = db.transaction(OUTBOX_STORE_NAME, "readwrite");
    const done = transactionDone(txn);
    apply(txn.objectStore(OUTBOX_STORE_NAME));
    await done;
  };

  return {
    readAll: async () => {
      const txn = db.transaction(OUTBOX_STORE_NAME, "readonly");
      const rows = await promisify<unknown[]>(
        txn.objectStore(OUTBOX_STORE_NAME).getAll() as IDBRequest<unknown[]>,
      );
      return rows as OutboxRecord[];
    },
    put: (record) => write((store) => void store.put(record)),
    remove: (clientKey) => write((store) => void store.delete(clientKey)),
    clear: () => write((store) => void store.clear()),
  };
}

/**
 * True for the errors that mean "this DATABASE HANDLE is dead", as opposed to
 * "this write failed".
 *
 * A browser force-closes an idle or memory-pressured IndexedDB connection
 * without warning; every later `db.transaction()` on the captured handle throws
 * `InvalidStateError`. A store that treats that as a normal write failure turns
 * itself into a permanent black hole for the rest of the app's life — the first
 * such error, and every subsequent tap, is silently lost while the screen still
 * says "Saved". The handle is disposable; the data on disk is not.
 *
 * Exported for the photo store (D-096), which shares the connection-death
 * problem because it shares the database.
 */
export function isDeadConnection(error: unknown): boolean {
  const name = (error as { name?: unknown } | null)?.name;
  return name === "InvalidStateError" || name === "TransactionInactiveError";
}

/**
 * The adapter that actually ships on a phone: it owns the connection rather
 * than borrowing one, and REOPENS it when the browser takes it away.
 */
export function reopeningOutboxStore(factory: IdbFactoryLike): OutboxStore {
  let db: IDBDatabase | undefined;
  let opening: Promise<IDBDatabase> | undefined;

  const connect = async (): Promise<IDBDatabase> => {
    if (db !== undefined) return db;
    opening ??= openIndexedDb(factory)
      .then((opened) => {
        // A connection the browser closes on us must not be reused, and a
        // version change from another tab must not block that tab forever.
        opened.onclose = () => {
          db = undefined;
        };
        opened.onversionchange = () => {
          opened.close();
          db = undefined;
        };
        db = opened;
        return opened;
      })
      .finally(() => {
        opening = undefined;
      });
    return opening;
  };

  const attempt = async <T>(run: (store: OutboxStore) => Promise<T>): Promise<T> => {
    const handle = await connect();
    try {
      return await run(indexedDbOutboxStore(handle));
    } catch (error) {
      if (!isDeadConnection(error)) throw error;
      db = undefined;
      // One retry against a fresh connection. If that fails too, the caller
      // hears about it — an enqueue that cannot be persisted must REJECT, not
      // pretend.
      const reopened = await connect();
      return run(indexedDbOutboxStore(reopened));
    }
  };

  return {
    readAll: () => attempt((store) => store.readAll()),
    put: (record) => attempt((store) => store.put(record)),
    remove: (key) => attempt((store) => store.remove(key)),
    clear: () => attempt((store) => store.clear()),
  };
}

export interface OpenedOutboxStore {
  readonly store: OutboxStore;
  /**
   * False when IndexedDB was unavailable and the queue is running in memory —
   * the chrome surfaces this so "queued" never over-promises.
   */
  readonly durable: boolean;
}

/**
 * Open the device's outbox store, degrading to memory rather than throwing. A
 * floor PWA that cannot persist must still WORK (and say so); refusing to load
 * would take a working scanner offline over a storage setting.
 */
export async function openOutboxStore(
  factory: IdbFactoryLike | undefined = typeof indexedDB === "undefined" ? undefined : indexedDB,
): Promise<OpenedOutboxStore> {
  if (factory === undefined) return { store: memoryOutboxStore(), durable: false };
  try {
    // Probe once so `durable` is a fact rather than a hope; the store the caller
    // gets owns its own (re-openable) connection.
    const probe = await openIndexedDb(factory);
    probe.close();
    return { store: reopeningOutboxStore(factory), durable: true };
  } catch {
    return { store: memoryOutboxStore(), durable: false };
  }
}
